import { ErrorMessage, Field, Form, Formik, getIn } from "formik";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Input from "../../components/form/Input";
import Spinner from "../../components/misc/Spinner";
import { DEV_MODE } from "../../constants";
import { Role, useAuthStore } from "../../stores/auth";
import { useCustomerPropertiesStore } from "../../stores/customer_properties";

const createInitialValues = (me, customer_property, isAddMode) => ({
  customer: {
    id: customer_property?.customer?.id || "",
    number: customer_property?.customer?.number || "",
    name: customer_property?.customer?.name || "",
    address: {
      street: customer_property?.customer?.addresses[0]?.street || "",
      zip: customer_property?.customer?.addresses[0]?.zip || "",
      city: customer_property?.customer?.addresses[0]?.city || "",
      district: customer_property?.customer?.addresses[0]?.district || "",
    },
    contact: {
      name: customer_property?.customer?.contacts[0]?.name || "",
    },
  },

  customer_property: {
    id: customer_property?.id || "",
    number: customer_property?.number || "",
    name: customer_property?.name || "",
    address: {
      street: customer_property?.addresses[0]?.street || "",
      zip: customer_property?.addresses[0]?.zip || "",
      city: customer_property?.addresses[0]?.city || "",
      district: customer_property?.addresses[0]?.district || "",
    },
    contact: {
      name: customer_property?.contacts[0]?.name || "",
    },
  },
});

function CustomerPropertyAddEdit() {
  const { t } = useTranslation();
  const params = useParams();
  const propertyId = Number.parseInt(params.propertyId);
  const isAddMode = !propertyId;
  const navigate = useNavigate();

  const [me, hasRole] = useAuthStore((state) => [state.me, state.hasRole], shallow);
  const canEdit = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER);

  const [byIds, , getById, updateOne] = useCustomerPropertiesStore(
    (state) => [state.byIds, state.allIds, state.getById, state.updateOne],
    shallow,
  );

  const property = Number.isInteger(propertyId) ? byIds[propertyId] : null;

  useEffect(() => {
    if (propertyId) {
      getById({ id: propertyId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);
  const handleSubmit = async (values, { setErrors }) => {
    console.log(values);
    const func = isAddMode ? alert("Not impl.") : updateOne;
    const { error } = await func({
      id: propertyId,
      number: values.customer_property.number,
      name: values.customer_property.name,
      address: values.customer_property.address,
    });

    if (error) {
      //error.detail.forEach(d => setErrors)
      alert(JSON.stringify(error.text));
      //console.log(error)
    } else {
      navigate(`/customer_properties`);
    }
  };

  if (Number.isInteger(propertyId) && !property) {
    return <Spinner />;
  }

  const canSubmit = canEdit;

  return (
    <div>
      <Breadcrumb
        items={[
          { label: t("customers.properties.properties"), to: "/customer_properties" },
          {
            label: isAddMode ? t("menu.add") : `${t("common.number_short")} ${property.number}`,
          },
        ]}
      />

      {!canSubmit && <div className="alert alert-danger">{t("permissions.can_not_edit")}</div>}

      <Formik
        initialValues={createInitialValues(me, property, isAddMode)}
        //validationSchema={createValidationSchema(t)}
        validateOnChange={true}
        validateOnBlur={false}
        enableReinitialize={false}
        validateOnMount={false}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setValues,
          setFieldValue,
          /* and other goodies */
        }) => (
          <Form>
            <div className="row">
              <div className="col">
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-city fa-lg fa-fw text-dark text-opacity-50 me-1"></i>{" "}
                    {t("customers.properties.property")}
                    {!!property?.import_id && <span className="float-end">Import ID: {property.import_id}</span>}
                  </div>
                  <div className="card-body">
                    <div className="row mb-3 gx-3">
                      <div className="col-9">
                        <label className="form-label" htmlFor="customer_property.name">
                          {t("customers.properties.name")}
                        </label>
                        <Field
                          component={Input}
                          name="customer_property.name"
                          className="form-control fs-13px h-45px"
                        />
                        <ErrorMessage
                          name="customer_property.name"
                          render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
                        />
                      </div>
                      <div className="col-3">
                        <label className="form-label" htmlFor="customer_property.number">
                          {t("customers.properties.number")}
                        </label>
                        <Field
                          component={Input}
                          name="customer_property.number"
                          className={`form-control fs-13px h-45px ${
                            getIn(errors, "customer_property.number") ? "is-invalid" : ""
                          }`}
                          disabled
                        />
                        <ErrorMessage
                          name="customer_property.number"
                          render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
                        />
                      </div>
                    </div>

                    <div className="row mb-3 gx-3">
                      <div className="col-12">
                        <label className="form-label" htmlFor="customer_property.address.street">
                          {t("customers.properties.street")}
                        </label>
                        <Field
                          component={Input}
                          name="customer_property.address.street"
                          className="form-control fs-13px h-45px"
                        />
                        <ErrorMessage
                          name="customer_property.address.street"
                          render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
                        />
                      </div>
                    </div>

                    <div className="row mb-3 gx-3">
                      <div className="col-5 col-md-4">
                        <label className="form-label" htmlFor="customer_property.address.zip">
                          {t("customers.properties.zip")}
                        </label>
                        <Field
                          component={Input}
                          name="customer_property.address.zip"
                          className="form-control fs-13px h-45px"
                        />
                        <ErrorMessage
                          name="customer_property.address.zip"
                          render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
                        />
                      </div>
                      <div className="col-7 col-md-8">
                        <label className="form-label" htmlFor="customer_property.address.city">
                          {t("customers.properties.city")}
                        </label>
                        <Field
                          component={Input}
                          name="customer_property.address.city"
                          className="form-control fs-13px h-45px"
                        />
                        <ErrorMessage
                          name="customer_property.address.city"
                          render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
                        />
                      </div>
                    </div>
                    <div className="row mb-3 gx-3">
                      <div className="col-12">
                        <label className="form-label" htmlFor="customer_property.address.district">
                          {t("customers.properties.district")}
                        </label>
                        <Field
                          component={Input}
                          name="customer_property.address.district"
                          className="form-control fs-13px h-45px"
                        />
                        <ErrorMessage
                          name="customer_property.address.district"
                          render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3"></div>
                  <div className="card-body">
                    <div className="btn btn-group d-flex">
                      <button className="btn btn-success w-100" disabled={isSubmitting || !canSubmit}>
                        {t("menu.submit")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-address-card fa-lg fa-fw text-dark text-opacity-50 me-1"></i>{" "}
                    {t("customers.customer")}
                  </div>
                  <div className="card-body">
                    <ul className="list-unstyled">
                      <li>
                        <Link to={`/customers/${values.customer.id}/edit`}>{values.customer.number}</Link>
                        <br />
                        <address key={values.customer.address.id} className="mt-5px">
                          {values.customer.name}
                          <br />
                          <span className="text-nowrap">{values.customer.address.street}</span>
                          <br />
                          {values.customer.address.zip} {values.customer.address.city}
                          <br />
                        </address>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            {DEV_MODE && (
              <pre
                className="m-t-xl"
                style={{
                  fontSize: "1rem",
                  padding: ".25rem .5rem",
                  overflowX: "scroll",
                }}
              >
                VALUES: {JSON.stringify(values, null, 2)}
                <br />
                ERRORS: {JSON.stringify(errors, null, 2)}
              </pre>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default CustomerPropertyAddEdit;
